import {
  Component,
  OnInit,
  ViewEncapsulation,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ContentChild,
  NgZone,
  forwardRef,
  AfterViewInit,
  HostListener,
  Renderer2,
} from '@angular/core';
import { trigger, transition } from '@angular/animations';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import {
  PgOnSwitchLabelDirective,
  PgOffSwitchLabelDirective,
} from './pg-switch-label.directive';
import { offLabelAnimations, onLabelAnimations } from './pg-label-animations';
import { SwitchStyles } from './switch-styles';
import { HammerInput } from '../core/events/gestures-annotations';
import * as Hammer from 'hammerjs';

export class PgSwitchChange {
  constructor(public source: PgSwitchComponent, public checked: boolean) {}
}

let uid = 0;

@Component({
  selector: 'pg-switch, grit-switch',
  templateUrl: './pg-switch.component.html',
  styleUrls: ['./pg-switch.component.scss'],
  encapsulation: ViewEncapsulation.None,
  exportAs: 'pgSwitch, gritSwitch',
  animations: [
    trigger('OffTrigger', offLabelAnimations),
    trigger('OnTrigger', onLabelAnimations),
    trigger('blockInitial', [transition(':enter', [])]),
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PgSwitchComponent),
      multi: true,
    },
  ],
})
export class PgSwitchComponent implements ControlValueAccessor, AfterViewInit {
  public offLabelState: 'in' | 'out' = 'in';
  public onLabelState: 'in' | 'out' = 'out';
  public isActive = false;

  private onChange = (_: any) => {};
  private onTouched = () => {};

  private _uniqueId: string = `pg-switch-${++uid}`;
  private _checked: boolean = false;
  private _dragging = false;

  private _thumbBarWidth: number;
  private _dragPercentage: number;
  private _previousChecked: boolean;

  @ViewChild('input', { static: true }) _inputElement: ElementRef<
    HTMLInputElement
  >;
  @ViewChild('switchBar', { static: true }) _switchBar: ElementRef;
  @ViewChild('thumb', { static: true }) _thumb: ElementRef;

  // leaving these incase we want to use them
  // for refeactored/refined angular animations
  @ViewChild('labelOffContent') _labelOff: ElementRef;
  @ViewChild('labelOnContent') _labelOn: ElementRef;

  @ContentChild(PgOffSwitchLabelDirective, { static: false })
  offLabel: PgOffSwitchLabelDirective;

  @ContentChild(PgOnSwitchLabelDirective, { static: false })
  onLabel: PgOnSwitchLabelDirective;

  @Input() id: string = this._uniqueId;
  @Input() labelPosition: 'top' | 'bottom' = 'top';
  @Input() name: string | null = null;
  @Input('aria-label') ariaLabel: string | null = null;
  @Input('aria-labelledby') ariaLabelledby: string | null = null;
  @Input() styleOverrides: SwitchStyles;

  // this currently does nothing but adds an unstyled class, wanted
  // to keep it for the time being incase we wanted to add a focus state.
  @HostBinding('class.pg-switch')
  readonly _classPgSwitch = true;

  @HostBinding('class.is-focused')
  _isFocused = false;

  @Input('checked')
  @HostBinding('class.checked')
  get checked(): boolean {
    return this._checked;
  }

  set checked(value) {
    this._checked = value;
    this._changeDetectorRef.markForCheck();
    this.offLabelState = !value ? 'in' : 'out';
    this.onLabelState = value ? 'in' : 'out';
  }

  get checkedStr() {
    return this.checked ? 'true' : 'false';
  }

  @Input('disabled')
  @HostBinding('class.disabled')
  disabled = false;

  @HostListener('touchstart', ['$event'])
  @HostListener('mousedown', ['$event'])
  onMouseDown(event) {
    event.preventDefault();
    this.renderer.addClass(this._switchBar.nativeElement, 'clicked');
    this.isActive = true;
  }

  @HostListener('touchend', ['$event'])
  @HostListener('mouseup', ['$event'])
  onMouseUp(event) {
    event.preventDefault();
    this.isActive = false;
  }

  @Output() readonly change: EventEmitter<PgSwitchChange> = new EventEmitter<
    PgSwitchChange
  >();

  @Output() readonly dragChange: EventEmitter<void> = new EventEmitter<void>();

  get inputId(): string {
    return `${this.id || this._uniqueId}-input`;
  }

  constructor(
    private elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit() {
    if (this.labelPosition === 'bottom') {
      this.elementRef.nativeElement.classList.add('bottom-label');
    }
    if (!this.offLabel || !this.onLabel) {
      this.elementRef.nativeElement.classList.add('no-labels');
    }
  }

  onFocus() {
    this._isFocused = true;
  }

  onBlur() {
    this.renderer.removeClass(this._switchBar.nativeElement, 'clicked');
    this._isFocused = false;
  }

  _onDragStart() {
    if (!this.disabled && !this._dragging) {
      const thumbEl = this._thumb.nativeElement;
      this._thumbBarWidth =
        this._switchBar.nativeElement.clientWidth - thumbEl.clientWidth - 8;

      thumbEl.classList.add('pg-thumb-dragging');

      this._previousChecked = this.checked;
      this._dragging = true;
    }
  }

  _onDrag(evnt: HammerInput) {
    if (this._dragging) {
      this._dragPercentage = this._getDragPercentage(evnt.deltaX);

      const dragX = (this._dragPercentage / 100) * this._thumbBarWidth;
      this._thumb.nativeElement.style.transform = `translateX(${dragX}px)`;

      this._getProperDragClasses();
    }
  }

  _onDragEnd() {
    if (this._dragging) {
      const newCheckedValue = this._dragPercentage > 50;

      if (newCheckedValue !== this.checked) {
        this.dragChange.emit();
        this.checked = newCheckedValue;
        this._emitChangeEvent();
      }

      // The drag should be stopped outside of the current event handler, otherwise the
      // click event will be fired before it and will revert the drag change.
      this._ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          if (this._dragging) {
            this._dragging = false;
            this._thumb.nativeElement.classList.remove('pg-thumb-dragging');
            this._switchBar.nativeElement.classList.remove(
              'pg-thumb-bar-dragging',
              'fifty'
            );

            // Reset the transform because the component will take care
            // of the thumb position after drag.
            this._thumb.nativeElement.style.transform = '';
          }
        })
      );
    }
  }

  /** Method being called whenever the underlying input emits a change event. */
  _onChange(event: Event) {
    event.stopPropagation();

    // if (!this._dragging) {
    //   this.toggleChange.emit();
    // }

    if (this._dragging) {
      this._inputElement.nativeElement.checked = this.checked;
      return;
    }

    // Sync the value from the underlying input element with the component instance.
    this.checked = this._inputElement.nativeElement.checked;

    this._emitChangeEvent();
  }

  writeValue(value: any): void {
    this.checked = !!value;
    this.onTouched();
    this.onChange(value);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  private _emitChangeEvent() {
    this.onChange(this.checked);
    this.change.emit(new PgSwitchChange(this, this.checked));
  }

  private _getDragPercentage(distance: number) {
    let percentage = (distance / this._thumbBarWidth) * 100;

    // When the toggle was initially checked, then we have to start the drag at the end.
    if (this._previousChecked) {
      percentage += 100;
    }

    return Math.max(0, Math.min(percentage, 100));
  }

  private _getProperDragClasses() {
    if (this._dragPercentage >= 50 && this._dragPercentage < 75) {
      this._switchBar.nativeElement.classList.remove('checked');
      this._switchBar.nativeElement.classList.add('fifty');
    } else if (this._dragPercentage >= 75 && this._dragPercentage <= 100) {
      this._switchBar.nativeElement.classList.add('checked');
    } else {
      this._switchBar.nativeElement.classList.remove('fifty');
    }
    this._switchBar.nativeElement.classList.add('pg-thumb-bar-dragging');
  }
}
